'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { SearchReplaceRecommendationsFragmentDoc } from '@packages/gql/generated/shopping/SearchReplaceRecommendationsFragmentDoc';
import { SearchReplaceProductsRecoSlider } from '@packages/recommendations/src/components/ProductsRecoSliders/SearchReplaceProductsRecoSlider/SearchReplaceProductsRecoSlider.client';
import { useIntl } from 'react-intl';
import { getSkuPart } from '@packages/modules/src/helpers/getSkuPart/getSkuPart';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { unmask } from '@packages/gql/src/betterMasking';

/* GraphQL */ `
  fragment SearchReplaceRecommendations on Product {
    hasSeries
    orderNumber
    promotion
    sku
  }
`;

export type SearchReplaceProps = {
  /**
   * The data from the product query needed for the `SearchReplaceProductsRecoSlider` component.
   */
  data: FragmentType<typeof SearchReplaceRecommendationsFragmentDoc>;
};

/**
 * This component is a wrapper for the `SearchReplaceProductsRecoSlider` component.
 * It encapsulates the data from the product query needed for the `SearchReplaceProductsRecoSlider` component and passes correct props.
 */
export const SearchReplaceRecommendations = ({ data }: SearchReplaceProps) => {
  const { sku, hasSeries, orderNumber, promotion } =
    unmask<typeof SearchReplaceRecommendationsFragmentDoc>(data);
  const { formatMessage } = useIntl();
  const {
    product: { hasTransparentSection },
  } = useConfig();

  return (
    <SearchReplaceProductsRecoSlider
      productId={`${orderNumber}${promotion}`}
      size={getSkuPart(sku, 'Size')}
      elementIndex={hasSeries ? 3 : 2}
      fetchOnThreshold
      pageTemplate="detailview"
      title={formatMessage({
        id: 'rde.search-replace-products.headline-fallback',
        defaultMessage: 'Ähnliche Artikel',
      })}
      key={`${orderNumber}3`}
      {...(hasTransparentSection && {
        backgroundColor: 'transparent',
      })}
    />
  );
};
